import { ModalOverlay } from "@components/atoms";
import { XCircle, Download } from "lucide-react";

interface QRTicketViewerProps {
  isOpen: boolean;
  onClose: () => void;
  eventName: string;
  eventImage?: string | null;
  localityName: string;
  ticketCount: number;
  eventDate: string;
  ticketCode: string;
  qrSrc: string;
}

/**
 * QR ticket viewer modal.
 * 335×440 white card with QR, event info, and download.
 * Close button (x-circle) is outside and above the modal.
 */
export function QRTicketViewer({
  isOpen,
  onClose,
  eventName,
  eventImage,
  localityName,
  ticketCount,
  eventDate,
  ticketCode,
  qrSrc,
}: QRTicketViewerProps) {
  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = qrSrc;
    link.download = `ticket-${ticketCode}.png`;
    link.click();
  };

  return (
    <ModalOverlay isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col items-center gap-3">
        {/* Close button — outside modal, top-right */}
        <div className="flex w-full justify-end max-w-[335px]">
          <button
            className="cursor-pointer border-none bg-transparent text-white"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <XCircle size={32} />
          </button>
        </div>

        {/* Modal card */}
        <div
          className="flex flex-col items-center rounded-2xl bg-white p-6 w-full max-w-[335px]"
          style={{ minHeight: 360 }}
        >
          {/* QR code */}
          <img
            src={qrSrc}
            alt={`QR ${ticketCode}`}
            className="object-contain w-full max-w-[245px]"
            style={{ aspectRatio: "1/1" }}
          />

          {/* Event info — 2 columns */}
          <div className="mt-4 flex w-full gap-3">
            {/* Left column — wider */}
            <div className="flex flex-1 flex-col gap-1">
              <span
                className="line-clamp-2 font-semibold text-primary-110"
                style={{ fontSize: 14 }}
              >
                {eventName}
              </span>
              <div className="flex items-center gap-1">
                <span className="text-neutral-110" style={{ fontSize: 10 }}>
                  N° de Tickets
                </span>
                <span className="text-primary-110" style={{ fontSize: 10 }}>
                  {ticketCount} - {localityName}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-neutral-110" style={{ fontSize: 10 }}>
                  Fecha
                </span>
                <span className="text-primary-110" style={{ fontSize: 10 }}>
                  {eventDate}
                </span>
              </div>
            </div>

            {/* Right column — event image (only when available) */}
            {eventImage ? (
              <img
                src={eventImage}
                alt={eventName}
                className="rounded-lg object-cover flex-shrink-0"
                style={{ width: 85, height: 85 }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <div
                className="rounded-lg bg-neutral-10 flex-shrink-0"
                style={{ width: 85, height: 85 }}
              />
            )}
          </div>
        </div>

        {/* Download button — outside modal, below */}
        <button
          className="flex h-[50px] w-[220px] cursor-pointer items-center justify-center gap-2 rounded-[36px] border border-primary-110 bg-secondary-120 font-semibold text-primary-110 transition-colors hover:opacity-90"
          style={{ fontSize: 14 }}
          onClick={handleDownload}
        >
          Descargar
          <Download size={24} />
        </button>
      </div>
    </ModalOverlay>
  );
}
